import CopyContent from '@/ui/components/CopyContent';
import { DataRow } from '@/ui/components/DataList';
import { currencyFormatter } from '@/utils/currencies';
import usFormatPhoneNumber from '@/utils/usFormatPhoneNumber';

import { getInsuranceFinanceFormatted } from '../utils';

import InsuranceCard from './InsuranceCard';
import { Props } from './types';

const InsuranceDetails = ({ insurancePolicy }: Props) => {
  const {
    carrier,
    type,
    priority_type,
    co_pay,
    co_pay_unit,
    last_verification,
    plan_end_date: plan_end_date_str,
    plan_start_date: plan_start_date_str,
    subscriber_id,
    plan,
    policyholder_first_name,
    policyholder_last_name,
    relationship,
    policyholder_dob,
    annual_deductible,
    co_insurance,
    out_of_pocket_maximum,
    insurer_address_line_1,
    insurer_address_line_2,
    insurer_city,
    insurer_state,
    insurer_zip_code,
    group_id,
    health_plan_id,
    rx_bin,
    rx_group,
    guarantor,
    provider_phone_number,
    insurance_card_images
  } = insurancePolicy;

  const { deductible_remaining, out_of_pocket_maximum_remaining_in_cents } =
    last_verification || {};

  const coPay = getInsuranceFinanceFormatted({
    currencyToFormat: co_pay,
    suffix: co_pay_unit ? `x ${co_pay_unit}` : ''
  });

  const oopMaxRemaining = getInsuranceFinanceFormatted({
    currencyToFormat: out_of_pocket_maximum_remaining_in_cents
  });

  const deductibleRemaining = getInsuranceFinanceFormatted({
    currencyToFormat: deductible_remaining
  });

  const oopMax = getInsuranceFinanceFormatted({ currencyToFormat: out_of_pocket_maximum });

  const annualDeductible = getInsuranceFinanceFormatted({
    currencyToFormat: annual_deductible,
    currencyFormatter
  });

  const getFullAddress = () => {
    const fullAddress = `${insurer_address_line_1 ?? ''} ${insurer_address_line_2 ?? ''} ${
      insurer_city ?? ''
    } ${insurer_state ?? ''} ${insurer_zip_code ?? ''}`;
    if (fullAddress.trim() === '') {
      return '-';
    }
    return fullAddress;
  };

  return (
    <div className="flex flex-col gap-2 data-row">
      <div>
        <DataRow
          label="Carrier:"
          tooltipContent="Carrier (member selection) is what the PM selects in the app signup flow">
          {carrier || '-'}
        </DataRow>
        <DataRow
          label="Plan:"
          tooltipContent="Plan (internal) is what an agent selects after a VOB, maps to Healthie's list of plans">
          {plan || '-'}
        </DataRow>
        <DataRow
          label="Subscriber ID:"
          tooltipContent={
            'The # also referred to as "Identification Number", "Member ID", "Patient ID", or "Policy number" on insurance cards'
          }>
          {subscriber_id ? <CopyContent content={subscriber_id} /> : '-'}
        </DataRow>
      </div>
      <div>
        <DataRow
          label="Type:"
          tooltipContent={
            'The originator of the policy,"Employer", "Medicaid", "Medicare", "Marketplace", "Unknown", or NoInsurance"'
          }>
          {type || '-'}
        </DataRow>
        <DataRow
          label="Order:"
          tooltipContent={
            'The order in which we bill plans, "Primary", "Secondary", "Tertiary+", "Inactive", or "Unknown" '
          }>
          {priority_type || '-'}
        </DataRow>
      </div>

      <div>
        <DataRow label="Plan Start:">{plan_start_date_str || '-'}</DataRow>
        <DataRow label="Plan End:">{plan_end_date_str || '-'}</DataRow>
      </div>

      <div className="flex flex-col gap-6 justify-start my-2 md:flex-row">
        <InsuranceCard label="Card front" imageUrl={insurance_card_images?.front_image_url} />
        <InsuranceCard label="Card back" imageUrl={insurance_card_images?.back_image_url} />
      </div>

      <div>
        <DataRow label="Policyholder First Name:">{policyholder_first_name || '-'}</DataRow>
        <DataRow label="Policyholder Last Name:">{policyholder_last_name || '-'}</DataRow>
        <DataRow label="Relationship:">{relationship || '-'}</DataRow>
        <DataRow label="DOB:">{policyholder_dob || '-'}</DataRow>
      </div>

      <hr />
      <div>
        <DataRow label="Co-pay:">{coPay}</DataRow>
        <DataRow label="Annual deductible:">{annualDeductible}</DataRow>
        <DataRow label="Co-insurance (percent):">{co_insurance ? `${co_insurance}%` : '-'}</DataRow>
        <DataRow label="OOP Max:">{oopMax}</DataRow>
        <DataRow label="OOP Max remaining:">{oopMaxRemaining}</DataRow>
        <DataRow label="Deductible remaining:">{deductibleRemaining}</DataRow>
      </div>

      <hr />
      <div>
        <DataRow label="Insurer Address:">{getFullAddress()}</DataRow>
        <DataRow label="Group ID:">{group_id || '-'}</DataRow>
        <DataRow label="Health Plan ID:">{health_plan_id || '-'}</DataRow>
        <DataRow label="Insurance Phone #:">
          {provider_phone_number ? (
            <a
              className="overflow-hidden text-ellipsis text-blue-600 w-full"
              target="_blank"
              rel="noreferrer"
              href={`tel:${provider_phone_number}`}>
              {usFormatPhoneNumber(provider_phone_number)}
            </a>
          ) : (
            '-'
          )}
        </DataRow>
        <DataRow label="Rx Bin:">{rx_bin || '-'}</DataRow>
        <DataRow label="Rx Group:">{rx_group || '-'}</DataRow>
        <DataRow label="Guarantor:">{guarantor || '-'}</DataRow>
      </div>
    </div>
  );
};

export default InsuranceDetails;
